import type { Router } from 'vue-router'
import { driver, type DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useAuthStore } from '../stores/auth'

// First-run onboarding tours (driver.js). Persistence is localStorage-only.
// - Admins: a "pending" flag is set at tenant registration; the tour shows once on the
//   first admin dashboard visit, then a per-role "done" flag stops it recurring.
// - Homeowners: created by an admin (no self-registration), so there is no pending flag —
//   the tour shows once on the first "My Dashboard" visit (done-gated only).
const PENDING_KEY = 'kapit_onboarding_pending'
const ADMIN_DONE_KEY = 'kapit_onboarding_done_v1'
const HOMEOWNER_DONE_KEY = 'kapit_onboarding_done_homeowner_v1'
const AUDITOR_DONE_KEY = 'kapit_onboarding_done_auditor_v1'
const GUARD_DONE_KEY = 'kapit_onboarding_done_guard_v1'

// Steps anchor on [data-tour="…"] elements. Steps whose element is missing/hidden
// (e.g. the collapsed sidebar on mobile) are filtered out before driving.
const ADMIN_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to Kapit 👋',
      description: "You're all set up. Here's a quick tour of your admin console — it takes under a minute.",
    },
  },
  {
    element: '[data-tour="dashboard-metrics"]',
    popover: {
      title: 'Your community at a glance',
      description: 'Households, users, outstanding dues, and anything pending your review — all here.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="quick-actions"]',
    popover: {
      title: 'Quick actions',
      description: 'Jump straight to billing, households, payment reviews, and reports.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-community"]',
    popover: {
      title: 'Community',
      description: 'Post announcements, track incidents, and manage amenities.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-directory"]',
    popover: {
      title: 'Directory',
      description: 'Set up your households, streets, and buildings. A good first step is adding your households here.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-billing"]',
    popover: {
      title: 'Billing & Collections',
      description: 'Configure dues, run monthly billing cycles, and review homeowner payments.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-gate"]',
    popover: {
      title: 'Gate & Visitors',
      description: 'Manage visitor passes and gate access for your community.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-admin"]',
    popover: {
      title: 'Administration',
      description: 'Invite users — guards, board members, staff — and manage your subscription.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="account-menu"]',
    popover: {
      title: "You're ready to go",
      description: 'Your account and settings live here — and you can replay this tour anytime from this menu.',
      side: 'bottom',
      align: 'end',
    },
  },
]

const HOMEOWNER_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to Kapit 👋',
      description: "Here's a quick tour of your homeowner portal — it takes under a minute.",
    },
  },
  {
    element: '[data-tour="ho-summary"]',
    popover: {
      title: 'Your home at a glance',
      description: 'Your current balance, next due date, unpaid bills, and any payments awaiting review — all here.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="ho-pay"]',
    popover: {
      title: 'Pay & upload proof',
      description: 'Settle your dues and upload your proof of payment here — an admin reviews and confirms it.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '[data-tour="nav-my-finance"]',
    popover: {
      title: 'My Finance',
      description: 'Your bills, payment history, credits, and vehicle stickers.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-my-access"]',
    popover: {
      title: 'My Access',
      description: 'Pre-register visitors, manage passes, and track your deliveries.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-my-community"]',
    popover: {
      title: 'My Community',
      description: 'Read community announcements and report incidents.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="account-menu"]',
    popover: {
      title: "You're all set",
      description: 'Your account and settings live here — and you can replay this tour anytime from this menu.',
      side: 'bottom',
      align: 'end',
    },
  },
]

const AUDITOR_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to Kapit 👋',
      description: "Here's a quick tour of your auditor console — it takes under a minute.",
    },
  },
  {
    element: '[data-tour="auditor-reports"]',
    popover: {
      title: 'Your reporting home',
      description: 'Statements of account, delinquency, and collections — this is where you land.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-billing"]',
    popover: {
      title: 'Billing & Collections',
      description: 'Read access to billing, payments, member and credit ledgers, and stickers for your review.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-oversight"]',
    popover: {
      title: 'Oversight',
      description: 'Run the annual audit and inspect the system audit log here.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-community"]',
    popover: {
      title: 'Community',
      description: 'Read community announcements and reported incidents.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="account-menu"]',
    popover: {
      title: "You're all set",
      description: 'Your account and settings live here — and you can replay this tour anytime from this menu.',
      side: 'bottom',
      align: 'end',
    },
  },
]

const GUARD_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to Kapit 👋',
      description: "This is your gate console — here's a 20-second tour.",
    },
  },
  {
    element: '[data-tour="guard-tiles"]',
    popover: {
      title: 'Everything, one tap away',
      description: 'Scan a pass, log a visitor, check a plate, record deliveries, and see alerts — all from here.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="guard-tile-scan"]',
    popover: {
      title: 'Scan a pass',
      description: "Start here to scan a visitor's QR pass and check them in or out.",
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="account-menu"]',
    popover: {
      title: "You're all set",
      description: 'Your account is here — and you can replay this tour anytime from this menu.',
      side: 'bottom',
      align: 'end',
    },
  },
]

function isVisible(selector: string): boolean {
  const el = document.querySelector(selector) as HTMLElement | null
  if (!el || el.offsetParent === null) return false
  // Require a real box. This drops anchors that are laid out but collapsed to ~0 size —
  // e.g. the sidebar at mobile widths (minimized-width:0), where highlighting an 8px sliver
  // would look broken. Size-only (not viewport position): driver.js scrolls to below-the-fold
  // anchors, so those must stay.
  const r = el.getBoundingClientRect()
  return r.width >= 24 && r.height >= 8
}

export function markOnboardingPending(): void {
  try {
    localStorage.setItem(PENDING_KEY, '1')
  } catch {
    /* storage unavailable — tour simply won't fire */
  }
}

function markDone(doneKey: string, clearPending: boolean): void {
  try {
    localStorage.setItem(doneKey, '1')
    if (clearPending) localStorage.removeItem(PENDING_KEY)
  } catch {
    /* ignore */
  }
}

function runTour(steps: DriveStep[], doneKey: string, clearPending: boolean): void {
  const visible = steps.filter((s) => !s.element || (typeof s.element === 'string' && isVisible(s.element)))
  if (visible.length === 0) return
  const d = driver({
    showProgress: true,
    allowClose: true,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    steps: visible,
  })
  d.drive()
  // Mark as seen the moment it starts, so it never auto-recurs regardless of how the user
  // exits (finish, close, or navigating away mid-tour). Replaying resets the flag.
  markDone(doneKey, clearPending)
}

// On a free/locked tenant, Billing & Collections and Gate & Visitors are Community-plan
// features — keep their tour steps (so the admin learns what's available) but mark them as
// paid rather than presenting them as usable now.
const GATED_TOUR_ELEMENTS = new Set(['[data-tour="nav-billing"]', '[data-tour="nav-gate"]'])

function adminStepsForPlan(): DriveStep[] {
  if (useAuthStore().featuresUnlocked) return ADMIN_STEPS
  return ADMIN_STEPS.map((step) =>
    step.element && typeof step.element === 'string' && GATED_TOUR_ELEMENTS.has(step.element) && step.popover
      ? { ...step, popover: { ...step.popover, description: `${step.popover.description ?? ''} Unlocks with the Community plan.` } }
      : step,
  )
}

export function startAdminTour(): void {
  runTour(adminStepsForPlan(), ADMIN_DONE_KEY, true)
}

export function startHomeownerTour(): void {
  runTour(HOMEOWNER_STEPS, HOMEOWNER_DONE_KEY, false)
}

export function startAuditorTour(): void {
  runTour(AUDITOR_STEPS, AUDITOR_DONE_KEY, false)
}

export function startGuardTour(): void {
  runTour(GUARD_STEPS, GUARD_DONE_KEY, false)
}

// Wait until the given anchors are laid out (dashboards load data async) so the step set is
// consistent rather than dropping a still-0-height anchor, then start.
function waitForAnchorsThenStart(ready: () => boolean, start: () => void, attempt: number): void {
  if (ready() || attempt >= 20) {
    start()
    return
  }
  setTimeout(() => waitForAnchorsThenStart(ready, start, attempt + 1), 150)
}

// Admin dashboard on mount: fire once for a freshly registered admin.
export function maybeStartAdminTour(): void {
  try {
    if (localStorage.getItem(PENDING_KEY) === '1' && localStorage.getItem(ADMIN_DONE_KEY) !== '1') {
      waitForAnchorsThenStart(
        () => isVisible('[data-tour="dashboard-metrics"]') && isVisible('[data-tour="quick-actions"]'),
        startAdminTour,
        0,
      )
    }
  } catch {
    /* ignore */
  }
}

// Homeowner dashboard on mount: fire once on the first visit (no registration hook, so
// done-gated only — existing homeowners also see it once).
export function maybeStartHomeownerTour(): void {
  try {
    if (localStorage.getItem(HOMEOWNER_DONE_KEY) !== '1') {
      waitForAnchorsThenStart(() => isVisible('[data-tour="ho-summary"]'), startHomeownerTour, 0)
    }
  } catch {
    /* ignore */
  }
}

// Auditor Reports page on mount: fire once on the first visit (done-gated only). The Reports
// page is shared with admins, so the caller must gate this to BOARD_AUDITOR.
export function maybeStartAuditorTour(): void {
  try {
    if (localStorage.getItem(AUDITOR_DONE_KEY) !== '1') {
      waitForAnchorsThenStart(() => isVisible('[data-tour="auditor-reports"]'), startAuditorTour, 0)
    }
  } catch {
    /* ignore */
  }
}

// Guard home hub on mount: fire once on the first visit (done-gated only). The guard-home
// route is GUARD-only, so no role gate is needed here.
export function maybeStartGuardTour(): void {
  try {
    if (localStorage.getItem(GUARD_DONE_KEY) !== '1') {
      waitForAnchorsThenStart(() => isVisible('[data-tour="guard-tiles"]'), startGuardTour, 0)
    }
  } catch {
    /* ignore */
  }
}

type ReplayConfig = { doneKey: string; landing: string; start: () => void; usesPending: boolean }

function replayConfigForRole(role?: string): ReplayConfig {
  if (role === 'HOMEOWNER') {
    return { doneKey: HOMEOWNER_DONE_KEY, landing: 'homeowner-dashboard', start: startHomeownerTour, usesPending: false }
  }
  if (role === 'BOARD_AUDITOR') {
    return { doneKey: AUDITOR_DONE_KEY, landing: 'reports', start: startAuditorTour, usesPending: false }
  }
  if (role === 'GUARD') {
    return { doneKey: GUARD_DONE_KEY, landing: 'guard-home', start: startGuardTour, usesPending: false }
  }
  // SUPER_ADMIN / ASSOCIATION_ADMIN
  return { doneKey: ADMIN_DONE_KEY, landing: 'dashboard', start: startAdminTour, usesPending: true }
}

// Replay from the account menu — role-aware: reset the right "done" flag and run that role's
// tour (navigating to its landing page first if we're elsewhere so the anchors exist).
export function requestReplay(router: Router, role?: string): void {
  const cfg = replayConfigForRole(role)
  try {
    localStorage.removeItem(cfg.doneKey)
    if (cfg.usesPending) localStorage.setItem(PENDING_KEY, '1')
  } catch {
    /* ignore */
  }
  if (router.currentRoute.value.name === cfg.landing) {
    setTimeout(() => cfg.start(), 100)
  } else {
    void router.push({ name: cfg.landing })
  }
}

export function useOnboardingTour() {
  return {
    markOnboardingPending,
    startAdminTour,
    startHomeownerTour,
    startAuditorTour,
    startGuardTour,
    maybeStartAdminTour,
    maybeStartHomeownerTour,
    maybeStartAuditorTour,
    maybeStartGuardTour,
    requestReplay,
  }
}
